import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services';
import type { Customer } from '../types';

interface AuthContextType {
  customer: Customer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    tenantCode: string;
  }) => Promise<void>;
  logout: () => void;
  refreshCustomer: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshCustomer = useCallback(async () => {
    try {
      const data = await authService.getMe();
      setCustomer(data);
      localStorage.setItem('loyalty_customer', JSON.stringify(data));
    } catch {
      setCustomer(null);
      localStorage.removeItem('loyalty_token');
      localStorage.removeItem('loyalty_customer');
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('loyalty_token');
    if (token) {
      refreshCustomer().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [refreshCustomer]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authService.login(email, password);
    localStorage.setItem('loyalty_token', response.token);
    localStorage.setItem('loyalty_customer', JSON.stringify(response.customer));
    setCustomer(response.customer);
  }, []);

  const register = useCallback(async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    tenantCode: string;
  }) => {
    const response = await authService.register(data);
    localStorage.setItem('loyalty_token', response.token);
    localStorage.setItem('loyalty_customer', JSON.stringify(response.customer));
    setCustomer(response.customer);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('loyalty_token');
    localStorage.removeItem('loyalty_customer');
    setCustomer(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      customer,
      isAuthenticated: !!customer,
      isLoading,
      login,
      register,
      logout,
      refreshCustomer,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
